import { useState, useMemo } from 'react';
import { ITimelineEvent } from '@/types/timeline';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'debug-time-machine-ui';
import {
  BarChart3,
  Clock,
  TrendingUp,
  AlertTriangle,
  Activity,
  Cpu,
  Database,
  Network,
  Zap,
  CheckCircle2,
} from 'lucide-react';

interface PerformanceMetricsProps {
  events: ITimelineEvent[];
}

interface MetricsSummary {
  totalEvents: number;
  errorRate: number;
  averageApiTime: number;
  performanceIssues: number;
  componentsCount: number;
  recentActivity: number;
}

interface ComponentMetrics {
  name: string;
  renderCount: number;
  averageRenderTime: number;
  errorCount: number;
  lastActivity: number;
}

interface ApiMetrics {
  endpoint: string;
  callCount: number;
  averageTime: number;
  errorRate: number;
  lastCall: number;
}

const PerformanceMetrics = ({ events }: PerformanceMetricsProps) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'components' | 'api' | 'performance'>(
    'overview'
  );

  // 메트릭 계산
  const metrics = useMemo((): MetricsSummary => {
    const totalEvents = events.length;
    const errors = events.filter(e => e.isError || e.severity === 'critical');
    const apiCalls = events.filter(e => e.type === 'api_call');
    const performanceEvents = events.filter(e => e.type === 'performance');
    const components = new Set(events.map(e => e.data?.componentName).filter(Boolean));
    const recentEvents = events.filter(e => Date.now() - e.timestamp < 300000); // 5분 이내

    const totalApiTime = apiCalls.reduce((sum, event) => {
      return sum + (event.data?.duration || 0);
    }, 0);

    return {
      totalEvents,
      errorRate: totalEvents > 0 ? (errors.length / totalEvents) * 100 : 0,
      averageApiTime: apiCalls.length > 0 ? totalApiTime / apiCalls.length : 0,
      performanceIssues: performanceEvents.filter(
        e => e.severity === 'warning' || e.severity === 'critical'
      ).length,
      componentsCount: components.size,
      recentActivity: recentEvents.length,
    };
  }, [events]);

  // 컴포넌트별 메트릭
  const componentMetrics = useMemo((): ComponentMetrics[] => {
    const componentMap = new Map<string, ComponentMetrics>();

    events.forEach(event => {
      const componentName = event.data?.componentName;
      if (!componentName) return;

      if (!componentMap.has(componentName)) {
        componentMap.set(componentName, {
          name: componentName,
          renderCount: 0,
          averageRenderTime: 0,
          errorCount: 0,
          lastActivity: 0,
        });
      }

      const metric = componentMap.get(componentName)!;
      metric.lastActivity = Math.max(metric.lastActivity, event.timestamp);

      if (event.type === 'state_change' || event.type === 'performance') {
        metric.renderCount++;
        if (event.data?.renderTime) {
          metric.averageRenderTime =
            (metric.averageRenderTime * (metric.renderCount - 1) + event.data.renderTime) /
            metric.renderCount;
        }
      }

      if (event.isError || event.severity === 'critical') {
        metric.errorCount++;
      }
    });

    return Array.from(componentMap.values()).sort((a, b) => b.lastActivity - a.lastActivity);
  }, [events]);

  // API 엔드포인트별 메트릭
  const apiMetrics = useMemo((): ApiMetrics[] => {
    const apiMap = new Map<string, ApiMetrics>();

    events
      .filter(e => e.type === 'api_call')
      .forEach(event => {
        const endpoint = event.data?.url || 'Unknown';

        if (!apiMap.has(endpoint)) {
          apiMap.set(endpoint, {
            endpoint,
            callCount: 0,
            averageTime: 0,
            errorRate: 0,
            lastCall: 0,
          });
        }

        const metric = apiMap.get(endpoint)!;
        metric.callCount++;
        metric.lastCall = Math.max(metric.lastCall, event.timestamp);

        if (event.data?.duration) {
          metric.averageTime =
            (metric.averageTime * (metric.callCount - 1) + event.data.duration) / metric.callCount;
        }

        if (event.isError) {
          metric.errorRate = (metric.errorRate * (metric.callCount - 1) + 100) / metric.callCount;
        } else {
          metric.errorRate = (metric.errorRate * (metric.callCount - 1)) / metric.callCount;
        }
      });

    return Array.from(apiMap.values()).sort((a, b) => b.lastCall - a.lastCall);
  }, [events]);

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  const getPerformanceColor = (value: number, thresholds: { good: number; warning: number }) => {
    if (value <= thresholds.good) return 'text-emerald-600';
    if (value <= thresholds.warning) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className='h-full flex flex-col'>
      <Tabs
        value={activeTab}
        onValueChange={(value: string) => setActiveTab(value as any)}
        className='flex-1 flex flex-col'
      >
        <TabsList className='grid w-full grid-cols-4 mb-6'>
          <TabsTrigger value='overview' className='flex items-center gap-2'>
            <BarChart3 className='w-4 h-4' />
            Overview
          </TabsTrigger>
          <TabsTrigger value='components' className='flex items-center gap-2'>
            <Cpu className='w-4 h-4' />
            Components
          </TabsTrigger>
          <TabsTrigger value='api' className='flex items-center gap-2'>
            <Network className='w-4 h-4' />
            API
          </TabsTrigger>
          <TabsTrigger value='performance' className='flex items-center gap-2'>
            <Zap className='w-4 h-4' />
            Performance
          </TabsTrigger>
        </TabsList>

        <div className='flex-1 overflow-hidden'>
          <TabsContent value='overview' className='h-full overflow-y-auto'>
            <div className='grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6'>
              {/* Total Events */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Total Events</CardTitle>
                  <Activity className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{metrics.totalEvents}</div>
                  <p className='text-xs text-muted-foreground'>
                    {metrics.recentActivity} in last 5 minutes
                  </p>
                </CardContent>
              </Card>

              {/* Error Rate */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Error Rate</CardTitle>
                  <AlertTriangle className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${getPerformanceColor(metrics.errorRate, { good: 1, warning: 5 })}`}
                  >
                    {formatPercent(metrics.errorRate)}
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    {events.filter(e => e.isError).length} errors total
                  </p>
                </CardContent>
              </Card>

              {/* Average API Time */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Avg API Time</CardTitle>
                  <Clock className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${getPerformanceColor(metrics.averageApiTime, { good: 200, warning: 1000 })}`}
                  >
                    {formatTime(metrics.averageApiTime)}
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    {events.filter(e => e.type === 'api_call').length} API calls
                  </p>
                </CardContent>
              </Card>

              {/* Performance Issues */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Performance Issues</CardTitle>
                  <TrendingUp className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${metrics.performanceIssues > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
                  >
                    {metrics.performanceIssues}
                  </div>
                  <p className='text-xs text-muted-foreground'>Performance warnings</p>
                </CardContent>
              </Card>

              {/* Components Count */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Components</CardTitle>
                  <Database className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{metrics.componentsCount}</div>
                  <p className='text-xs text-muted-foreground'>Active components</p>
                </CardContent>
              </Card>

              {/* Health Status */}
              <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                  <CardTitle className='text-sm font-medium'>Health Status</CardTitle>
                  <CheckCircle2 className='w-4 h-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${
                      metrics.errorRate < 1 && metrics.performanceIssues === 0
                        ? 'text-emerald-600'
                        : metrics.errorRate < 5
                          ? 'text-amber-600'
                          : 'text-red-600'
                    }`}
                  >
                    {metrics.errorRate < 1 && metrics.performanceIssues === 0
                      ? 'Good'
                      : metrics.errorRate < 5
                        ? 'Warning'
                        : 'Critical'}
                  </div>
                  <p className='text-xs text-muted-foreground'>Overall system health</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value='components' className='h-full overflow-y-auto'>
            <div className='space-y-4'>
              {componentMetrics.length > 0 ? (
                componentMetrics.map((component, index) => (
                  <Card key={component.name}>
                    <CardHeader>
                      <div className='flex items-center justify-between'>
                        <CardTitle className='text-lg'>{component.name}</CardTitle>
                        <div className='flex items-center gap-2'>
                          {component.errorCount > 0 && (
                            <Badge variant='destructive'>{component.errorCount} errors</Badge>
                          )}
                          <Badge variant='secondary'>{component.renderCount} renders</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className='grid grid-cols-3 gap-4 text-sm'>
                        <div>
                          <p className='text-muted-foreground'>Avg Render Time</p>
                          <p
                            className={`font-medium ${getPerformanceColor(component.averageRenderTime, { good: 16, warning: 50 })}`}
                          >
                            {formatTime(component.averageRenderTime)}
                          </p>
                        </div>
                        <div>
                          <p className='text-muted-foreground'>Total Renders</p>
                          <p className='font-medium'>{component.renderCount}</p>
                        </div>
                        <div>
                          <p className='text-muted-foreground'>Last Activity</p>
                          <p className='font-medium'>
                            {component.lastActivity
                              ? new Date(component.lastActivity).toLocaleTimeString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className='text-center py-8 text-muted-foreground'>
                  <Cpu className='w-12 h-12 mx-auto mb-4 opacity-50' />
                  <p>No component metrics available</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value='api' className='h-full overflow-y-auto'>
            <div className='space-y-4'>
              {apiMetrics.length > 0 ? (
                apiMetrics.map((api, index) => (
                  <Card key={api.endpoint}>
                    <CardHeader>
                      <div className='flex items-center justify-between'>
                        <CardTitle className='text-sm font-mono truncate'>{api.endpoint}</CardTitle>
                        <div className='flex items-center gap-2'>
                          {api.errorRate > 0 && (
                            <Badge variant='destructive'>
                              {formatPercent(api.errorRate)} errors
                            </Badge>
                          )}
                          <Badge variant='secondary'>{api.callCount} calls</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className='grid grid-cols-3 gap-4 text-sm'>
                        <div>
                          <p className='text-muted-foreground'>Avg Response Time</p>
                          <p
                            className={`font-medium ${getPerformanceColor(api.averageTime, { good: 200, warning: 1000 })}`}
                          >
                            {formatTime(api.averageTime)}
                          </p>
                        </div>
                        <div>
                          <p className='text-muted-foreground'>Total Calls</p>
                          <p className='font-medium'>{api.callCount}</p>
                        </div>
                        <div>
                          <p className='text-muted-foreground'>Last Call</p>
                          <p className='font-medium'>
                            {api.lastCall ? new Date(api.lastCall).toLocaleTimeString() : 'Never'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className='text-center py-8 text-muted-foreground'>
                  <Network className='w-12 h-12 mx-auto mb-4 opacity-50' />
                  <p>No API metrics available</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value='performance' className='h-full overflow-y-auto'>
            <div className='space-y-6'>
              {/* Performance Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
                    <div className='text-center'>
                      <p className='text-2xl font-bold text-emerald-600'>
                        {
                          events.filter(
                            e =>
                              e.type === 'performance' &&
                              (!e.data?.renderTime || e.data.renderTime <= 16)
                          ).length
                        }
                      </p>
                      <p className='text-sm text-muted-foreground'>Fast Renders (&lt;16ms)</p>
                    </div>
                    <div className='text-center'>
                      <p className='text-2xl font-bold text-amber-600'>
                        {
                          events.filter(
                            e =>
                              e.type === 'performance' &&
                              e.data?.renderTime > 16 &&
                              e.data?.renderTime <= 50
                          ).length
                        }
                      </p>
                      <p className='text-sm text-muted-foreground'>Slow Renders (16-50ms)</p>
                    </div>
                    <div className='text-center'>
                      <p className='text-2xl font-bold text-red-600'>
                        {
                          events.filter(e => e.type === 'performance' && e.data?.renderTime > 50)
                            .length
                        }
                      </p>
                      <p className='text-sm text-muted-foreground'>Very Slow Renders (&gt;50ms)</p>
                    </div>
                    <div className='text-center'>
                      <p className='text-2xl font-bold text-blue-600'>
                        {Math.round(
                          events
                            .filter(e => e.type === 'performance' && e.data?.renderTime)
                            .reduce((sum, e) => sum + e.data.renderTime, 0) /
                            events.filter(e => e.type === 'performance' && e.data?.renderTime)
                              .length || 0
                        )}
                        ms
                      </p>
                      <p className='text-sm text-muted-foreground'>Avg Render Time</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Events */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Performance Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    {events
                      .filter(e => e.type === 'performance')
                      .slice(-10)
                      .reverse()
                      .map((event, index) => (
                        <div
                          key={event.id}
                          className='flex items-center justify-between p-3 border rounded-lg'
                        >
                          <div className='flex items-center gap-3'>
                            <div
                              className={`w-2 h-2 rounded-full ${
                                !event.data?.renderTime || event.data.renderTime <= 16
                                  ? 'bg-emerald-500'
                                  : event.data.renderTime <= 50
                                    ? 'bg-amber-500'
                                    : 'bg-red-500'
                              }`}
                            />
                            <div>
                              <p className='font-medium'>
                                {event.data?.componentName || 'Unknown Component'}
                              </p>
                              <p className='text-sm text-muted-foreground'>
                                {new Date(event.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <div className='text-right'>
                            <p className='font-medium'>
                              {event.data?.renderTime ? formatTime(event.data.renderTime) : 'N/A'}
                            </p>
                            <p className='text-sm text-muted-foreground'>render time</p>
                          </div>
                        </div>
                      ))}
                  </div>
                  {events.filter(e => e.type === 'performance').length === 0 && (
                    <div className='text-center py-8 text-muted-foreground'>
                      <Zap className='w-12 h-12 mx-auto mb-4 opacity-50' />
                      <p>No performance events recorded</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default PerformanceMetrics;
